import { Link } from '@tanstack/react-router'
import { ArrowLeft, Gift } from 'lucide-react'
import { motion, Variants } from 'framer-motion'

import { PlanCards } from '@/components/billing/PlanCards'
import { ProductMark } from '@/components/shell/Sidebar'
import { DEFAULT_FREE_CREDITS, WHAT_CREDITS_BUY } from '@/features/billing/plans'
import { useAuth } from '@/features/auth/useAuth'

/**
 * What this costs, for someone who has not signed in.
 *
 * Public on purpose. Pricing behind a login is a dark pattern in a product people are being
 * asked to pay for, and it is also the page a visitor wants *before* they create an account.
 * Rendered outside the application shell, so it works with no session at all.
 */
export function PricingPage() {
  const { user } = useAuth()

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.1 }
    }
  }

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, y: 0, 
      transition: { type: 'spring', stiffness: 100, damping: 15 } 
    }
  }

  return (
    <div className="min-h-full bg-ground">
      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2 text-ink">
            <ProductMark className="h-6 w-6" />
            <span className="text-base font-semibold">RepoPilot</span>
          </Link>
          <Link
            to={user ? '/chat' : '/'}
            className="flex items-center gap-1.5 text-sm text-ink-dim underline-offset-2 hover:text-ink hover:underline"
          >
            <ArrowLeft aria-hidden="true" className="h-4 w-4" />
            {user ? 'Back to chat' : 'Back'}
          </Link>
        </div>
      </header>

      <motion.main 
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-20px' }}
        className="mx-auto max-w-5xl px-4 py-10 sm:px-6"
      >
        <motion.div variants={itemVariants}>
          <h1 className="text-2xl font-semibold text-ink sm:text-3xl">Pay for what you use</h1>
          <p className="mt-2 max-w-2xl text-base text-ink-muted">
            No subscription. Credits are spent on what a request actually costs to run, measured
            from the tokens the model reports — not a flat fee per question, and nothing at all
            for a request that fails before it reaches the model.
          </p>
        </motion.div>

        <motion.p
          variants={itemVariants}
          className="mt-6 flex flex-wrap items-center gap-2 rounded-lg border border-ok/25
            bg-ok-wash px-4 py-3 text-sm text-ink-dim"
        >
          <Gift aria-hidden="true" className="h-4 w-4 shrink-0 text-ok" />
          <span>
            <span className="font-medium text-ink">
              {DEFAULT_FREE_CREDITS} free credits when you sign up.
            </span>{' '}
            Enough for a few questions or a full pull-request review. No card required.
          </span>
        </motion.p>

        <motion.section variants={itemVariants} className="mt-10">
          <h2 className="text-md font-semibold text-ink">What a credit buys</h2>
          <dl className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {WHAT_CREDITS_BUY.map((item) => (
              <motion.div variants={itemVariants} key={item.label} className="rounded-lg border border-line bg-surface px-4 py-3">
                <dt className="text-sm text-ink-dim">{item.label}</dt>
                <dd className="mt-1 text-lg font-semibold tabular-nums text-ink">
                  ~{item.credits} credits
                </dd>
              </motion.div>
            ))}
          </dl>
          <p className="mt-2 text-xs text-ink-muted">
            Measured from real usage. One credit is ₹1.
          </p>
        </motion.section>

        <motion.section variants={itemVariants} className="mt-10">
          <h2 className="text-md font-semibold text-ink">Credit packs</h2>
          <PlanCards className="mt-4" />
          <p className="mt-4 text-sm text-ink-muted">
            Card and UPI payments are being finished. Until then every account runs on its free
            credits, and nothing is charged.
          </p>
        </motion.section>

        <motion.section variants={itemVariants} className="mt-12 rounded-xl border border-line bg-surface p-6">
          <h2 className="text-md font-semibold text-ink">How charging works</h2>
          <ul className="mt-3 space-y-2 text-sm text-ink-muted">
            <li>
              <span className="text-ink-dim">Credits are held, then charged.</span> Before a
              request runs, enough credits are set aside to cover it. When it finishes, you are
              charged what it actually used and the rest is returned.
            </li>
            <li>
              <span className="text-ink-dim">A failed request is not a charge.</span> If the model
              is never reached, nothing is taken.
            </li>
            <li>
              <span className="text-ink-dim">Reading your repositories is free.</span> Credits pay
              for the assistant's thinking, not for listing a repository or opening an issue.
            </li>
            <li>
              <span className="text-ink-dim">Every charge is itemised.</span> Your credit history
              shows each movement, and the executions page shows the call behind it.
            </li>
          </ul>
        </motion.section>
      </motion.main>
    </div>
  )
}
